import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Handles the Emergent Google Auth redirect back to /dashboard#session_id=xxx.
 * Rendered synchronously by the App router when the URL hash contains "session_id=".
 * Uses a useRef flag (not state) to prevent double-processing under React StrictMode.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/callback", {
          session_id: sessionId,
        });
        loginWith(data.token, data.user);
        // Remove the fragment from the URL and go to dashboard
        window.history.replaceState({}, "", "/dashboard");
        toast.success(`Signed in as ${data.user.name.split(" ")[0]} · via Google`);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [loginWith, navigate]);

  return (
    <div className="min-h-screen bg-gh-navy text-white grid place-items-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center shadow-lg shadow-emerald-500/30">
          <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex items-center gap-2 text-white/70 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
          Finishing sign-in…
        </div>
      </div>
    </div>
  );
}
